import cn from 'classnames';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';

import { AISummaryTabHeader } from '@features/campaigns/performance/components/AISummaryTabHeader/AISummaryTabHeader.tsx';
import { EmptyState } from '@shared/components/EmptyState/EmptyState.tsx';
import { ICON_VARIANTS } from '@shared/components/Icon/IconMapper.ts';
import { MarkdownRenderer } from '@shared/components/MarkdownRenderer/MarkdownRenderer.tsx';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { useCopyToClipboard } from '@shared/hooks/useCopyToClipboard.tsx';

import styles from './AISummary.module.scss';

interface AISummaryProps {
  summary: string;
  generationDate: string;
  isSummaryGenerated: boolean;
  isSummaryLoading: boolean;
  campaignName: string;
}

export function AISummary({
  summary,
  generationDate,
  isSummaryGenerated,
  isSummaryLoading,
  campaignName,
}: AISummaryProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);
  const { handleCopyClick } = useCopyToClipboard();

  const showNoResults = !isSummaryLoading && !isSummaryGenerated && !summary;
  const showSummary = !isSummaryLoading && isSummaryGenerated && summary;

  return (
    <section className={styles.wrapper} data-testid='ai-summary-section'>
      <Accordion>
        <AccordionTab
          headerTemplate={AISummaryTabHeader({
            iconVariant: ICON_VARIANTS.paper,
            accordionTitle: t('page.campaignDetails.performanceTab.accordionSummaryAITitle', { campaignName }),
            completed: isSummaryGenerated,
            isLoading: isSummaryLoading,
          })}
        >
          <section className={styles.contentWrapper}>
            <div className={styles.content}>
              {isSummaryLoading && (
                <>
                  <Skeleton
                    width={'50%'}
                    height='1rem'
                    style={{ marginBottom: '1rem' }}
                    data-testid='skeleton'
                  ></Skeleton>
                  <Skeleton width={'100%'} height='1rem' data-testid='skeleton'></Skeleton>
                  <Skeleton width={'100%'} height='1rem' data-testid='skeleton'></Skeleton>
                  <Skeleton width={'100%'} height='1rem' data-testid='skeleton'></Skeleton>
                </>
              )}

              {showNoResults && (
                <EmptyState
                  title={t('page.campaignDetails.performanceTab.noSummaryTitle')}
                  subtitle={t('page.campaignDetails.performanceTab.noSummarySubtitle')}
                />
              )}

              {showSummary && (
                <div className={styles.scrollableContent}>
                  <MarkdownRenderer content={summary} className={styles.markdownContent} />
                </div>
              )}
            </div>
            {isSummaryGenerated && (
              <footer className={styles.footer}>
                <Button
                  className={styles.copy}
                  outlined
                  severity='secondary'
                  onClick={() => handleCopyClick(summary, { isMarkdown: true })}
                >
                  <i className={cn('pi pi-copy', styles.icon)} /> {t('page.campaignDetails.performanceTab.copyButton')}
                </Button>
                <span className={styles.dateText}>{generationDate}</span>
              </footer>
            )}
          </section>
        </AccordionTab>
      </Accordion>
    </section>
  );
}
